import { z } from 'zod';
import { DESIGN_PRIORITIES, DESIGN_REQUEST_STATUSES, DESIGN_REQUEST_TYPES } from './design-workflow.js';
import { isValidTimeZone } from './report-dto.js';
import { smartReportDateSchema } from './smart-report.js';
/** Which timestamp / date the range applies to. `due` is the EFFECTIVE due date (ETA when set, else requested). */
export const DESIGN_REPORT_DATE_BASES = ['created', 'submitted', 'released', 'due', 'updated'];
export const DESIGN_REPORT_PREVIEW_MAX_PAGE_SIZE = 100;
export const DESIGN_REPORT_EXPORT_MAX_ROWS = 20_000;
export const DESIGN_REPORT_PDF_MAX_ROWS = 2_000;
/** Drafts are private working copies and never appear in reports. */
export const DESIGN_REPORT_STATUSES = DESIGN_REQUEST_STATUSES.filter((s) => s !== 'DRAFT');
/** Allowlist: no request body, content, notes, instructions, links or cancel reason. */
export const DESIGN_REPORT_COLUMNS = [
    { key: 'number', label: 'Request' },
    { key: 'title', label: 'Title' },
    { key: 'requestType', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'priority', label: 'Priority' },
    { key: 'region', label: 'Region' },
    { key: 'requester', label: 'Requester' },
    { key: 'regionalCoordinator', label: 'Regional coordinator' },
    { key: 'designLead', label: 'Design lead' },
    { key: 'designer', label: 'Designer' },
    { key: 'createdAt', label: 'Created' },
    { key: 'submittedAt', label: 'Submitted' },
    { key: 'releasedAt', label: 'Released' },
    { key: 'cancelledAt', label: 'Cancelled' },
    { key: 'dueDate', label: 'Requested due date' },
    { key: 'etaDate', label: 'ETA' },
    { key: 'effectiveDueDate', label: 'Effective due date' },
    { key: 'overdue', label: 'Overdue' },
    { key: 'approvalRound', label: 'Approval round' },
    { key: 'updatedAt', label: 'Last updated' },
];
const COLUMN_KEYS = DESIGN_REPORT_COLUMNS.map((c) => c.key);
export const DESIGN_REPORT_DEFAULT_COLUMNS = [
    'number', 'title', 'requestType', 'status', 'priority', 'region', 'requester', 'designer', 'createdAt', 'releasedAt', 'effectiveDueDate', 'overdue',
];
const idList = z.array(z.string().uuid()).max(100);
export const designReportQuerySchema = z
    .object({
    date: smartReportDateSchema,
    dateBasis: z.enum(DESIGN_REPORT_DATE_BASES).default('created'),
    statuses: z.array(z.enum(DESIGN_REPORT_STATUSES)).max(25).optional(),
    priorities: z.array(z.enum(DESIGN_PRIORITIES)).max(5).optional(),
    requestTypes: z.array(z.enum(DESIGN_REQUEST_TYPES)).max(15).optional(),
    regionIds: idList.optional(),
    requesterIds: idList.optional(),
    designerIds: idList.optional(),
    designLeadIds: idList.optional(),
    /** Only requests past their effective due date that are still open. */
    overdueOnly: z.boolean().optional(),
    q: z.string().trim().min(1).max(50).optional(),
    columns: z.array(z.enum(COLUMN_KEYS)).min(1).max(COLUMN_KEYS.length).refine((c) => new Set(c).size === c.length, 'duplicate columns').default([...DESIGN_REPORT_DEFAULT_COLUMNS]),
    page: z.number().int().min(1).max(100).default(1),
    pageSize: z.number().int().min(1).max(DESIGN_REPORT_PREVIEW_MAX_PAGE_SIZE).default(25),
    tz: z.string().trim().min(1).max(64).refine(isValidTimeZone, { message: 'tz must be an IANA time zone' }).optional(),
})
    .strict();
//# sourceMappingURL=smart-report-design.js.map